import React, { useContext, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ForgotContext } from '../../contexts/ForgotContext';
import { FORGOT_ACTIONS } from './ForgotConstants';
import User from '../../models/User';
import Error from '../../utils/Error';
import { UserContext } from '../../contexts/UserContext';
import { RegisterContext } from '../../contexts/RegisterContext';

const { SUCCESS, LOGIN } = FORGOT_ACTIONS;

export const ForgotStep3: React.FC = () => {
	const { t } = useTranslation();
	const { login } = useContext(UserContext);
	const { username, password, setPassword, sessionId, reset } = useContext(RegisterContext);
	const { dispatch, setLoading } = useContext(ForgotContext);
	const [password2, setPassword2] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const passwordField = useRef<HTMLInputElement>(null);

	const resetPassword = useCallback(
		async (e) => {
			e.preventDefault();

			setLoading(true);

			if (password === password2) {
				const response = await User.resetPassword(sessionId, username, password);

				if (response.isSuccess()) {
					try {
						await login({ username, password });
						reset();
						setLoading(false);
						setErrorMessage(null);

						dispatch({ type: SUCCESS });
					} catch (error) {
						dispatch({ type: LOGIN });
					}
				} else {
					setLoading(false);
					setPassword(null);
					setPassword2(null);
					setErrorMessage(Error.getErrorMessage(response.getErrorCode()));
					passwordField.current?.focus();
				}
			} else {
				setLoading(false);
				setPassword(null);
				setPassword2(null);
				setErrorMessage(t('Forgot.Step3.Error.PasswordMismatch'));
				passwordField.current?.focus();
			}
		},
		[dispatch, login, password, password2, reset, sessionId, setLoading, setPassword, t, username]
	);

	const onPasswordChange = useCallback((e) => setPassword(e.target.value), [setPassword]);
	const onPassword2Change = useCallback((e) => setPassword2(e.target.value), [setPassword2]);

	return (
		<form className={'forgot-password-form-step-3 form-large'} onSubmit={resetPassword}>
			<div className={'message'}>{t('Forgot.Step3.Message')}</div>
			{errorMessage && <div className={'alert alert-danger'}>{errorMessage}</div>}
			<div className={'form-group'}>
				<label htmlFor={'forgot-step3-password'}>{t('Forgot.Step3.Password')}</label>
				<input
					id={'forgot-step3-password'}
					ref={passwordField}
					autoFocus
					type={'password'}
					autoCapitalize={'false'}
					autoCorrect={'false'}
					autoComplete={'new-password'}
					className={'form-control'}
					value={password || ''}
					onChange={onPasswordChange}
				/>
			</div>
			<div className={'form-group'}>
				<label htmlFor={'forgot-step3-password2'}>{t('Forgot.Step3.PasswordConfirm')}</label>
				<input
					id={'forgot-step3-password2'}
					type={'password'}
					autoCapitalize={'false'}
					autoCorrect={'false'}
					autoComplete={'new-password'}
					className={'form-control'}
					value={password2 || ''}
					onChange={onPassword2Change}
				/>
			</div>
			<div className={'form-buttons'}>
				<input type={'submit'} className={'btn btn-primary'} value={String(t('Forgot.Step3.Submit'))} />
			</div>
		</form>
	);
};
